import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CheckIcon from "@mui/icons-material/Check";
import { FONT_HEADING } from "../../../theme";
import { getShopifyWebhooks } from "../../../APICalling/shopifyApi";
import { getAllSetting } from "../../../APICalling/settingApi";
import {
  SHOPIFY_SHOP_URL_HEADER_KEY,
  buildShopifyShopUrlHeader,
  EXTENSION_IDENTIFIER,
} from "../../../config/serviceConstant";

const cellSx = { fontSize: 14, py: 1.2, px: 1.5, borderColor: "#e4e4e7" };
const headCellSx = {
  ...cellSx,
  fontWeight: 600,
  color: "#71717a",
  bgcolor: "#fafafa",
  fontSize: 13,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

/** Extract setting_id from a webhook address URL */
const parseSettingId = (address) => {
  if (!address) return null;
  try {
    const url = new URL(address);
    return url.searchParams.get("setting_id");
  } catch {
    const match = address.match(/setting_id=([^&]+)/);
    return match ? match[1] : null;
  }
};

/** Build a readable label from a topic like "customers/create" */
const formatTopic = (topic) => {
  if (!topic) return "Unknown";
  return topic
    .replace(/\//g, " ")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

export default function Webhook({
  orgId,
  apiKey,
  dataCenterUrl,
  dataCenter,
  shopifyStores,
  setSnack,
}) {
  const [loading, setLoading] = useState(true);
  const [webhooks, setWebhooks] = useState([]);
  const [settingsMap, setSettingsMap] = useState({});

  // Row clicked → opens a read-only summary dialog for that webhook
  const [viewTarget, setViewTarget] = useState(null);

  // Filters
  const [searchText, setSearchText] = useState("");
  const [serviceFilter, setServiceFilter] = useState("");
  const [moduleFilter, setModuleFilter] = useState("");
  const [shopFilter, setShopFilter] = useState("");
  const [groupBy, setGroupBy] = useState("");

  const storeDomains = (shopifyStores || [])
    .map((s) => s?.domain)
    .filter(Boolean);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (storeDomains.length === 0) {
        setWebhooks([]);
        setSettingsMap({});
        setLoading(false);
        return;
      }

      // Fetch webhooks + settings for every connected store in parallel.
      const perStoreResults = await Promise.all(
        storeDomains.map(async (storeDomain) => {
          const storeHeaders = {
            orgid: orgId,
            connname: EXTENSION_IDENTIFIER,
            apikey: apiKey,
            datacenterurl: dataCenterUrl,
            datacenter: dataCenter,
            [SHOPIFY_SHOP_URL_HEADER_KEY]: buildShopifyShopUrlHeader(storeDomain),
          };
          const storeSettingAuth = { orgId, apiKey, dataCenterUrl, shop: storeDomain };
          const [wh, settings] = await Promise.all([
            getShopifyWebhooks(storeHeaders),
            getAllSetting(storeSettingAuth),
          ]);
          return {
            storeDomain,
            webhooks: Array.isArray(wh) ? wh : [],
            settings: Array.isArray(settings) ? settings : [],
          };
        })
      );

      // Merge webhooks from every store, tagging each with its source store
      // so the Shop column can fall back to that when the setting lookup fails.
      const mergedWebhooks = [];
      const sMap = {};
      const seenWebhookIds = new Set();
      for (const { storeDomain, webhooks: storeWebhooks, settings: storeSettings } of perStoreResults) {
        for (const s of storeSettings) {
          const id = s.setting_id || s.settingId || s.id;
          if (id && !sMap[id]) sMap[id] = s;
        }
        for (const wh of storeWebhooks) {
          const whId = wh.id || `${storeDomain}:${wh.topic}:${wh.address}`;
          if (seenWebhookIds.has(whId)) continue;
          seenWebhookIds.add(whId);
          mergedWebhooks.push({ ...wh, _sourceStore: storeDomain });
        }
      }

      setWebhooks(mergedWebhooks);
      setSettingsMap(sMap);
    } catch (err) {
      console.error("Failed to fetch webhooks:", err);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, apiKey, dataCenterUrl, dataCenter, storeDomains.join("|")]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /** Get setting info for a webhook */
  const getSettingInfo = (webhook) => {
    const settingId = parseSettingId(webhook.address);
    if (!settingId) return { settingId: null, setting: null };
    return { settingId, setting: settingsMap[settingId] || null };
  };

  /** Resolve module and service from setting schema */
  const resolveSettingDetails = (setting) => {
    if (!setting) return {};
    let schema = setting.settingSchema || setting.setting_schema || {};
    if (typeof schema === "string") {
      try {
        schema = JSON.parse(schema);
      } catch {
        schema = {};
      }
    }
    return {
      module: setting.moduleApiName || setting.module_name || schema.module || "-",
      service: schema.service || "-",
      trigger: schema.trigger || "-",
      shop: schema.shop || "-",
    };
  };

  // Flatten webhooks + setting details into a single filterable/groupable shape
  const enrichedRows = useMemo(() => {
    return webhooks.map((wh, idx) => {
      const { settingId, setting } = getSettingInfo(wh);
      const details = resolveSettingDetails(setting);
      const topic = wh.topic || "";
      const shop =
        (setting && details.shop !== "-" ? details.shop : wh._sourceStore) || "-";
      const service = setting ? details.service : "-";
      const module = setting ? details.module : "-";
      return {
        key: wh.id || `${idx}-${topic}`,
        // Tag the raw payload with the linked setting so the details dialog
        // can read schema.rows etc. without re-fetching.
        raw: { ...wh, _linkedSetting: setting || null },
        topic,
        topicLabel: formatTopic(topic),
        service,
        module,
        shop,
        settingId: settingId || "",
        address: wh.address || "",
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webhooks, settingsMap]);

  // Distinct options for filter dropdowns (derived from current data)
  const serviceOptions = useMemo(
    () => [...new Set(enrichedRows.map((r) => r.service).filter((v) => v && v !== "-"))].sort(),
    [enrichedRows]
  );
  const moduleOptions = useMemo(
    () => [...new Set(enrichedRows.map((r) => r.module).filter((v) => v && v !== "-"))].sort(),
    [enrichedRows]
  );
  const shopOptions = useMemo(
    () => [...new Set(enrichedRows.map((r) => r.shop).filter((v) => v && v !== "-"))].sort(),
    [enrichedRows]
  );

  // Apply search + filters
  const filteredRows = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return enrichedRows.filter((r) => {
      if (serviceFilter && r.service !== serviceFilter) return false;
      if (moduleFilter && r.module !== moduleFilter) return false;
      if (shopFilter && r.shop !== shopFilter) return false;
      if (!q) return true;
      return (
        r.topic.toLowerCase().includes(q) ||
        r.topicLabel.toLowerCase().includes(q) ||
        r.service.toLowerCase().includes(q) ||
        r.module.toLowerCase().includes(q) ||
        r.shop.toLowerCase().includes(q) ||
        r.settingId.toLowerCase().includes(q) ||
        r.address.toLowerCase().includes(q)
      );
    });
  }, [enrichedRows, searchText, serviceFilter, moduleFilter, shopFilter]);

  // Group rows by a chosen column (or put everything under "All" if no group)
  const groupedRows = useMemo(() => {
    if (!groupBy) return [{ name: null, rows: filteredRows }];
    const map = new Map();
    for (const r of filteredRows) {
      const key = r[groupBy] || "(Unassigned)";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }
    return [...map.entries()]
      .sort(([a], [b]) => String(a).localeCompare(String(b)))
      .map(([name, rows]) => ({ name, rows }));
  }, [filteredRows, groupBy]);

  const activeFilterCount =
    (searchText ? 1 : 0) +
    (serviceFilter ? 1 : 0) +
    (moduleFilter ? 1 : 0) +
    (shopFilter ? 1 : 0) +
    (groupBy ? 1 : 0);

  const clearAll = () => {
    setSearchText("");
    setServiceFilter("");
    setModuleFilter("");
    setShopFilter("");
    setGroupBy("");
  };

  const serviceChipStyle = (service) => ({
    fontSize: 12,
    height: 22,
    bgcolor: service === "Zoho CRM" ? "#eff6ff" : "#f4f4f5",
    color: service === "Zoho CRM" ? "#1d4ed8" : "#71717a",
  });

  const renderRow = (r) => (
    <TableRow
      key={r.key}
      hover
      onClick={() => setViewTarget(r)}
      sx={{
        cursor: "pointer",
        "&:last-child td": { borderBottom: 0 },
      }}
    >
      <TableCell sx={cellSx}>
        <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
          {r.topicLabel}
        </Typography>
        <Typography sx={{ fontSize: 12, color: "#a1a1aa" }}>{r.topic}</Typography>
      </TableCell>
      <TableCell sx={cellSx}>
        {r.service !== "-" ? (
          <Chip label={r.service} size="small" sx={serviceChipStyle(r.service)} />
        ) : (
          <Typography sx={{ fontSize: 13, color: "#a1a1aa" }}>-</Typography>
        )}
      </TableCell>
      <TableCell sx={cellSx}>
        <Typography sx={{ fontSize: 14 }}>{r.module}</Typography>
      </TableCell>
      <TableCell sx={cellSx}>
        <Typography sx={{ fontSize: 13, color: "#71717a" }}>{r.shop}</Typography>
      </TableCell>
      <TableCell sx={cellSx}>
        <Typography
          sx={{
            fontSize: 13,
            fontFamily: "monospace",
            color: r.settingId ? "#09090b" : "#a1a1aa",
          }}
        >
          {r.settingId || "-"}
        </Typography>
      </TableCell>
      <TableCell sx={{ ...cellSx, maxWidth: 260 }}>
        <Tooltip title={r.address} arrow>
          <Typography
            sx={{
              fontSize: 12,
              fontFamily: "monospace",
              color: "#71717a",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: 260,
            }}
          >
            {r.address || "-"}
          </Typography>
        </Tooltip>
      </TableCell>
    </TableRow>
  );

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
        <Box>
          <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: 18, fontWeight: 600, letterSpacing: "-0.02em" }}>
            Webhooks
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#71717a", mt: "2px" }}>
            All Shopify webhooks registered by this extension and their linked settings.
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchData} disabled={loading} size="small">
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Filter / search / group controls */}
      {!loading && webhooks.length > 0 && (
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            alignItems: "center",
            flexWrap: "wrap",
            mt: 2,
          }}
        >
          <TextField
            size="small"
            placeholder="Search topic, module, shop, setting id…"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 17, color: "#a1a1aa" }} />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 260, flex: 1 }}
          />

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel sx={{ fontSize: 13 }}>Service</InputLabel>
            <Select
              label="Service"
              value={serviceFilter}
              onChange={(e) => setServiceFilter(e.target.value)}
              sx={{ fontSize: 13 }}
            >
              <MenuItem value="" sx={{ fontSize: 13 }}>All services</MenuItem>
              {serviceOptions.map((s) => (
                <MenuItem key={s} value={s} sx={{ fontSize: 13 }}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel sx={{ fontSize: 13 }}>Module</InputLabel>
            <Select
              label="Module"
              value={moduleFilter}
              onChange={(e) => setModuleFilter(e.target.value)}
              sx={{ fontSize: 13 }}
            >
              <MenuItem value="" sx={{ fontSize: 13 }}>All modules</MenuItem>
              {moduleOptions.map((m) => (
                <MenuItem key={m} value={m} sx={{ fontSize: 13 }}>{m}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel sx={{ fontSize: 13 }}>Shop</InputLabel>
            <Select
              label="Shop"
              value={shopFilter}
              onChange={(e) => setShopFilter(e.target.value)}
              sx={{ fontSize: 13 }}
            >
              <MenuItem value="" sx={{ fontSize: 13 }}>All shops</MenuItem>
              {shopOptions.map((sh) => (
                <MenuItem key={sh} value={sh} sx={{ fontSize: 13 }}>{sh}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel sx={{ fontSize: 13 }}>Group by</InputLabel>
            <Select
              label="Group by"
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              sx={{ fontSize: 13 }}
            >
              <MenuItem value="" sx={{ fontSize: 13 }}>None</MenuItem>
              <MenuItem value="service" sx={{ fontSize: 13 }}>Service</MenuItem>
              <MenuItem value="module" sx={{ fontSize: 13 }}>Module</MenuItem>
              <MenuItem value="shop" sx={{ fontSize: 13 }}>Shop</MenuItem>
              <MenuItem value="topic" sx={{ fontSize: 13 }}>Topic</MenuItem>
            </Select>
          </FormControl>

          {activeFilterCount > 0 && (
            <Button
              size="small"
              onClick={clearAll}
              sx={{ textTransform: "none", fontSize: 13, color: "#ef4444" }}
            >
              Clear ({activeFilterCount})
            </Button>
          )}
        </Box>
      )}

      {loading ? (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <CircularProgress size={28} sx={{ color: "#71717a" }} />
          <Typography sx={{ fontSize: 14, color: "#71717a", mt: 1.5 }}>
            Loading webhooks...
          </Typography>
        </Box>
      ) : webhooks.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8, color: "#71717a" }}>
          <Typography sx={{ fontFamily: '"Inter", sans-serif', fontSize: 16, fontWeight: 600, color: "#09090b", mb: "6px" }}>
            No webhooks found
          </Typography>
          <Typography sx={{ fontSize: 14 }}>
            Webhooks will appear here once field mappings are created.
          </Typography>
        </Box>
      ) : filteredRows.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8, color: "#71717a" }}>
          <Typography sx={{ fontSize: 15, fontWeight: 500, color: "#09090b", mb: "6px" }}>
            No webhooks match the current filters
          </Typography>
          <Button size="small" onClick={clearAll} sx={{ textTransform: "none", fontSize: 13 }}>
            Clear filters
          </Button>
        </Box>
      ) : (
        <TableContainer
          sx={{
            mt: 2,
            border: "1px solid #e4e4e7",
            borderRadius: "8px",
            overflow: "hidden",
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>Topic</TableCell>
                <TableCell sx={headCellSx}>Service</TableCell>
                <TableCell sx={headCellSx}>Module</TableCell>
                <TableCell sx={headCellSx}>Shop</TableCell>
                <TableCell sx={headCellSx}>Setting ID</TableCell>
                <TableCell sx={headCellSx}>Webhook URL</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groupedRows.map((group, gi) => (
                <React.Fragment key={group.name ?? `grp-${gi}`}>
                  {group.name !== null && (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        sx={{
                          ...cellSx,
                          bgcolor: "#f4f4f5",
                          fontWeight: 600,
                          fontSize: 13,
                          color: "#52525b",
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                        }}
                      >
                        {group.name}{" "}
                        <Typography component="span" sx={{ fontSize: 12, color: "#a1a1aa", ml: 1 }}>
                          ({group.rows.length})
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {group.rows.map(renderRow)}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && webhooks.length > 0 && (
        <Typography sx={{ fontSize: 13, color: "#a1a1aa", mt: 1.5, textAlign: "right" }}>
          {filteredRows.length} of {webhooks.length} webhook{webhooks.length !== 1 ? "s" : ""}
        </Typography>
      )}

      {/* ── Read-only details dialog (opens when a row is clicked) ── */}
      <WebhookDetailsDialog
        target={viewTarget}
        onClose={() => setViewTarget(null)}
        formatTopic={formatTopic}
        serviceChipStyle={serviceChipStyle}
      />
    </Box>
  );
}

// ─── Helpers used inside the details dialog ────────────────────────────────

function SummaryPill({ label, value }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        fontSize: 13,
        color: "#3f3f46",
        bgcolor: "#f4f4f5",
        border: "1px solid #e4e4e7",
        borderRadius: "6px",
        px: "10px",
        py: "5px",
        minWidth: 0,
      }}
    >
      <Box component="span" sx={{ fontWeight: 600 }}>
        {label}:
      </Box>{" "}
      <Box
        component="span"
        sx={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: 220,
        }}
        title={value}
      >
        {value || "—"}
      </Box>
    </Box>
  );
}

function ViewCell({ label, value, borderLeft, borderTop, spanFull, copyable }) {
  const [copied, setCopied] = useState(false);
  const canCopy = copyable && value && value !== "—";

  const handleCopy = async () => {
    if (!canCopy) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(String(value));
      } else {
        const ta = document.createElement("textarea");
        ta.value = String(value);
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      /* ignore */
    }
  };

  return (
    <Box
      sx={{
        px: 2,
        py: 1.25,
        gridColumn: spanFull ? "1 / -1" : "auto",
        borderLeft: borderLeft ? "1px solid #f4f4f5" : "none",
        borderTop: borderTop ? "1px solid #f4f4f5" : "none",
        "@media (max-width: 599.95px)": { borderLeft: "none" },
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 600,
          color: "#71717a",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          mb: "4px",
        }}
      >
        {label}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 14,
            color: "#09090b",
            wordBreak: "break-word",
            flex: 1,
            minWidth: 0,
          }}
        >
          {value || "—"}
        </Typography>
        {canCopy && (
          <Tooltip title={copied ? "Copied" : "Copy"}>
            <IconButton
              size="small"
              onClick={handleCopy}
              sx={{
                width: 26,
                height: 26,
                color: copied ? "#16a34a" : "#71717a",
                flexShrink: 0,
                "&:hover": { color: "#09090b", bgcolor: "#f4f4f5" },
              }}
            >
              {copied ? (
                <CheckIcon sx={{ fontSize: 16 }} />
              ) : (
                <ContentCopyOutlinedIcon sx={{ fontSize: 15 }} />
              )}
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}

// ─── Details dialog ────────────────────────────────────────────────────────

function WebhookDetailsDialog({ target, onClose, formatTopic, serviceChipStyle }) {
  // Parse the linked setting (if any) to surface its trigger + field rows
  const linkedSchema = useMemo(() => {
    const raw =
      target?.raw?._linkedSetting?.settingSchema ||
      target?.raw?._linkedSetting?.setting_schema ||
      null;
    if (!raw) return null;
    if (typeof raw === "object") return raw;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }, [target]);

  if (!target) return null;

  const rows = Array.isArray(linkedSchema?.rows) ? linkedSchema.rows : [];

  return (
    <Dialog
      open={!!target}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: "14px",
          maxHeight: "90vh",
          height: "90vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
          borderBottom: "1px solid #e4e4e7",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: FONT_HEADING,
              fontSize: 17,
              fontWeight: 700,
              color: "#09090b",
              letterSpacing: "-0.02em",
            }}
          >
            Webhook Details
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#71717a", mt: "2px" }}>
            Read-only — registered Shopify webhook and the sync rule it
            triggers.
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "#71717a" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Body — only scrolling region */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          px: 3,
          py: 2.5,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {/* Context pills */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          <SummaryPill label="Topic" value={formatTopic(target.topic)} />
          {target.service !== "-" && (
            <SummaryPill label="Service" value={target.service} />
          )}
          {target.module !== "-" && (
            <SummaryPill label="Module" value={target.module} />
          )}
          <SummaryPill label="Shop" value={target.shop || "—"} />
        </Box>

        {/* Summary card */}
        <Box
          sx={{
            border: "1px solid #e4e4e7",
            borderRadius: "10px",
            bgcolor: "#fff",
            overflow: "hidden",
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          }}
        >
          <ViewCell label="Topic" value={target.topic || "—"} />
          <ViewCell
            label="Topic label"
            value={formatTopic(target.topic)}
            borderLeft
          />
          <ViewCell
            label="Service"
            value={target.service !== "-" ? target.service : "—"}
            borderTop
          />
          <ViewCell
            label="Zoho module"
            value={target.module !== "-" ? target.module : "—"}
            borderTop
            borderLeft
          />
          <ViewCell
            label="Shop"
            value={target.shop || "—"}
            borderTop
            copyable
          />
          <ViewCell
            label="Setting id"
            value={target.settingId || "—"}
            borderTop
            borderLeft
            copyable
          />
          <ViewCell
            label="Webhook id"
            value={target.raw?.id ? String(target.raw.id) : "—"}
            borderTop
            copyable
          />
          <ViewCell
            label="Format"
            value={target.raw?.format || "—"}
            borderTop
            borderLeft
          />
          <ViewCell
            label="API version"
            value={target.raw?.api_version || "—"}
            borderTop
          />
          <ViewCell
            label="Created at"
            value={fmtIso(target.raw?.created_at)}
            borderTop
            borderLeft
          />
          <ViewCell
            label="Updated at"
            value={fmtIso(target.raw?.updated_at)}
            borderTop
          />
          {linkedSchema?.triggerApiName && (
            <ViewCell
              label="Trigger"
              value={linkedSchema.triggerApiName}
              borderTop
              borderLeft
            />
          )}
          <ViewCell
            label="Webhook URL"
            value={target.address || "—"}
            borderTop
            spanFull
            copyable
          />
        </Box>

        {/* Optional: field mapping from the linked sync rule */}
        {rows.length > 0 && (
          <Box
            sx={{
              border: "1px solid #e4e4e7",
              borderRadius: "10px",
              bgcolor: "#fff",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1.25,
                bgcolor: "#fafafa",
                borderBottom: "1px solid #f4f4f5",
              }}
            >
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#09090b" }}>
                Linked Sync Rule Mapping
              </Typography>
              <Chip
                label={`${rows.length} field${rows.length === 1 ? "" : "s"}`}
                size="small"
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  height: 20,
                  bgcolor: "#f4f4f5",
                  color: "#52525b",
                }}
              />
            </Box>

            {/* Headers (2-col) */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                fontSize: 12,
                fontWeight: 600,
                color: "#71717a",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                borderBottom: "1px solid #f4f4f5",
                bgcolor: "#fff",
              }}
            >
              {[0, 1].map((c) => (
                <Box
                  key={c}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 24px 1fr",
                    gap: 1.25,
                    alignItems: "center",
                    px: 2,
                    py: 0.75,
                    borderLeft: c === 1 ? "1px solid #f4f4f5" : "none",
                    "@media (max-width: 599.95px)": {
                      borderLeft: "none",
                      display: c === 1 ? "none" : "grid",
                    },
                  }}
                >
                  <Box>Zoho field</Box>
                  <Box />
                  <Box>Shopify field</Box>
                </Box>
              ))}
            </Box>

            {/* Rows in a 2-col grid */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              }}
            >
              {rows.map((row, i) => (
                <Box
                  key={i}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 24px 1fr",
                    gap: 1.25,
                    alignItems: "center",
                    px: 2,
                    py: 1,
                    borderTop: i >= 2 ? "1px solid #f4f4f5" : "none",
                    borderLeft: i % 2 === 1 ? "1px solid #f4f4f5" : "none",
                    "@media (max-width: 599.95px)": {
                      borderLeft: "none",
                      borderTop: i === 0 ? "none" : "1px solid #f4f4f5",
                    },
                    minWidth: 0,
                  }}
                >
                  <Tooltip title={row?.Zoho || ""} placement="top-start" arrow>
                    <Typography
                      sx={{
                        fontSize: 14,
                        color: "#09090b",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row?.Zoho || "—"}
                    </Typography>
                  </Tooltip>
                  <ArrowBackIcon
                    sx={{ fontSize: 15, color: "#a1a1aa", justifySelf: "center" }}
                  />
                  <Tooltip title={row?.Shopify || ""} placement="top-start" arrow>
                    <Typography
                      sx={{
                        fontSize: 14,
                        color: "#09090b",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {row?.Shopify || "—"}
                    </Typography>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          justifyContent: "flex-end",
          px: 3,
          py: 1.75,
          borderTop: "1px solid #e4e4e7",
          bgcolor: "#fafafa",
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: 14,
            color: "#3f3f46",
            borderColor: "#e4e4e7",
            "&:hover": { borderColor: "#a1a1aa", bgcolor: "#fff" },
          }}
        >
          Close
        </Button>
      </Box>
    </Dialog>
  );
}

// Format an ISO string in the user's locale; falls back to "—" or the raw
// value when parsing fails.
function fmtIso(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    return d.toLocaleString([], {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
